import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BarcodePicker from "./BarcodePicker";
import FollowUpPatientPicker from "./FollowUpPatientPicker";
import { patientIdOf, patientName } from "../email-center/EmailPatientPicker";

const FollowUpFilters = ({
  batchId = "",
  onBatchIdChange,
  selectedPatient = null,
  onSelectedPatientChange,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedPatientLabel = useMemo(() => {
    if (!selectedPatient) {
      return "All Participants";
    }
    return patientName(selectedPatient);
  }, [selectedPatient]);

  return (
    <>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", lg: "center" }}
        sx={{ pb: 2, pt: 1 }}
      >
        <BarcodePicker
          value={batchId}
          onChange={(nextValue) => onBatchIdChange?.(nextValue)}
          sx={{ width: 240 }}
        />
        <Button
          variant={selectedPatient ? "contained" : "outlined"}
          size="small"
          startIcon={<MenuIcon fontSize="small" />}
          endIcon={<KeyboardArrowDownIcon fontSize="small" />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Select participant"
          sx={{
            width: 240,
            height: 40,
            position: "relative",
            justifyContent: "center",
            px: 4,
            "& .MuiButton-startIcon": {
              position: "absolute",
              left: 12,
              margin: 0,
            },
            "& .MuiButton-endIcon": {
              position: "absolute",
              right: 12,
              margin: 0,
            },
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selectedPatientLabel}
          </Box>
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="text"
          onClick={onClear}
          disabled={!batchId && !selectedPatient}
          sx={{ alignSelf: { xs: "flex-end", lg: "center" } }}
        >
          Clear
        </Button>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: anchorEl ? anchorEl.clientWidth : 240,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 0,
            border: "1px solid #000",
          },
        }}
      >
        <FollowUpPatientPicker
          selectedPatientId={patientIdOf(selectedPatient)}
          onSelectPatient={(patient, scope) => {
            if (scope === "patient") {
              onSelectedPatientChange?.(patient);
            } else if (scope === "all") {
              onSelectedPatientChange?.(null);
            }
            setAnchorEl(null);
          }}
          includeAll
          allLabel="All Participants"
          allDescription="Show all follow-up records"
          showHeader={false}
          height={520}
        />
      </Popover>
    </>
  );
};

export default FollowUpFilters;
